<template>
    <div class="collapse border-t border-b">
        <input
            id="productInfoDescription"
            v-model="descriptionState"
            type="checkbox"
            aria-labelledby="description-tab-label"
        >
        <div class="collapse-title py-5 px-4">
            <div class="flex items-center justify-between">
                <span id="description-tab-label" class="text-lg font-semibold">{{ $t('product.detail.tabs.description') }}</span>

                <button
                    class="btn btn-circle border-none shadow-none btn-sm"
                    :class="{
                        'bg-base-300': descriptionState,
                        'bg-transparent': !descriptionState,
                    }"
                    :aria-expanded="descriptionState"
                    aria-controls="description-content"
                    @click="descriptionState = !descriptionState"
                >
                    <span class="sr-only">{{ $t('product.detail.tabs.description') }}</span>
                    <MiscIcon v-show="!descriptionState" name="chevron-down" />
                    <MiscIcon v-show="descriptionState" name="chevron-up" />
                </button>
            </div>
        </div>
        <div id="description-content" class="collapse-content">
            <div :class="{ 'p-5': descriptionState }">
                <ProductDetailDescription />
            </div>
        </div>
    </div>

    <div class="collapse border-t -mt-px">
        <input
            id="productInfoReviews"
            v-model="reviewsState"
            type="checkbox"
            aria-labelledby="review-tab-label"
        >
        <div class="collapse-title py-5 px-4">
            <div class="flex items-center justify-between">
                <span id="review-tab-label" class="text-lg font-semibold">{{ $t('product.detail.tabs.reviews') }}</span>

                <button
                    class="btn btn-circle border-none shadow-none btn-sm"
                    :class="{
                        'bg-base-300': reviewsState,
                        'bg-transparent': !reviewsState,
                    }"
                    :aria-expanded="reviewsState"
                    aria-controls="review-content"
                    @click="reviewsState = !reviewsState"
                >
                    <span class="sr-only">{{ $t('product.detail.tabs.reviews') }}</span>
                    <MiscIcon v-show="!reviewsState" name="chevron-down" />
                    <MiscIcon v-show="reviewsState" name="chevron-up" />
                </button>
            </div>
        </div>
        <div id="review-content" ref="reviewsElement" class="collapse-content !border-b">
            <div :class="{ 'p-5': reviewsState }">
                <LazyProductDetailReviews v-if="initReviews" />
            </div>
        </div>
    </div>

    <div
        v-if="product?.translated?.customFields?.greenfeed_link != null && product?.translated?.customFields?.greenfeed_link.trim() !== ''"
        class="collapse border-t -mt-px"
    >
        <input
            id="productInfoGreenfeed"
            v-model="greenfeedState"
            type="checkbox"
            aria-labelledby="greenfeed-tab-label"
        >
        <div class="collapse-title py-5 px-4">
            <div class="flex items-center justify-between">
                <span id="greenfeed-tab-label" class="text-lg font-semibold">{{ $t('product.detail.tabs.greenFeedReport') }}</span>

                <button
                    class="btn btn-circle border-none shadow-none btn-sm"
                    :class="{
                        'bg-base-300': greenfeedState,
                        'bg-transparent': !greenfeedState,
                    }"
                    :aria-expanded="greenfeedState"
                    aria-controls="greenfeed-content"
                    @click="greenfeedState = !greenfeedState"
                >
                    <span class="sr-only">{{ $t('product.detail.tabs.greenFeedReport') }}</span>
                    <MiscIcon v-show="!greenfeedState" name="chevron-down" />
                    <MiscIcon v-show="greenfeedState" name="chevron-up" />
                </button>
            </div>
        </div>
        <div id="greenfeed-content" class="collapse-content">
            <div v-if="greenfeedState" class="flex flex-col sm:flex-row gap-8 px-0 py-8">
                <MiscImage
                    v-if="product?.extensions?.customFieldsMedia?.greenfeed_link_image?.url"
                    :src="product!.extensions!.customFieldsMedia!.greenfeed_link_image!.url!"
                    class="w-full sm:w-[280px] aspect-[3/2] object-cover flex-shrink-0"
                    loading="lazy"
                />
                <div class="flex flex-col gap-2">
                    <p v-if="product?.translated?.customFields?.greenfeed_link_headline" class="font-semibold text-base">
                        {{ product.translated!.customFields!.greenfeed_link_headline }}
                    </p>
                    <p v-if="product?.translated?.customFields?.greenfeed_link_text" class="text-sm">
                        {{ product.translated!.customFields!.greenfeed_link_text }}
                    </p>
                    <MiscLink
                        :to="product?.translated?.customFields?.greenfeed_link"
                        external
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        class="btn btn-accent self-start mt-8 sm:mt-auto"
                    >
                        {{ $t('product.detail.tabs.greenFeedReportLink') }}
                    </MiscLink>
                </div>
            </div>
        </div>
    </div>

    <div
        v-if="hasWarrantyLabel"
        class="collapse border-t -mt-px"
    >
        <input
            id="productInfoWarranty"
            v-model="warrantyState"
            type="checkbox"
            aria-labelledby="warranty-tab-label"
        >
        <div class="collapse-title py-5 px-4">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-wrap">
                    <span id="warranty-tab-label" class="text-lg font-semibold">{{ $t('product.detail.tabs.manufacturerWarranty') }}</span>
                    <div
                        v-if="generatedCompactSvg"
                        class="inline-flex p-2 border border-border-content rounded-xs bg-white"
                        style="width: 120px; height: auto;"
                        v-html="generatedCompactSvg"
                    />
                </div>

                <button
                    class="btn btn-circle border-none shadow-none btn-sm"
                    :class="{
                        'bg-base-300': warrantyState,
                        'bg-transparent': !warrantyState,
                    }"
                    :aria-expanded="warrantyState"
                    aria-controls="warranty-content"
                    @click="warrantyState = !warrantyState"
                >
                    <span class="sr-only">{{ $t('product.detail.tabs.manufacturerWarranty') }}</span>
                    <MiscIcon v-show="!warrantyState" name="chevron-down" />
                    <MiscIcon v-show="warrantyState" name="chevron-up" />
                </button>
            </div>
        </div>
        <div id="warranty-content" class="collapse-content">
            <div v-if="warrantyState" class="flex flex-col gap-6 px-0 py-8">
                <MiscImage
                    v-if="warrantyData?.mediaUrl && !warrantyData.useGeneratedSvg"
                    :src="warrantyData.mediaUrl"
                    class="w-full max-w-[580px]"
                    loading="lazy"
                    alt="EU Garantielabel"
                />
                <div
                    v-else-if="warrantyData?.useGeneratedSvg && generatedFullSvg"
                    class="w-full max-w-[580px]"
                    v-html="generatedFullSvg"
                />
                <MiscLink
                    v-if="warrantyData?.conditionsLink"
                    :to="warrantyData.conditionsLink"
                    external
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    class="flex link link-primary items-center text-sm gap-[6px] py-1 px-3"
                >
                    {{ $t('product.detail.tabs.warrantyConditionsLink') }}
                    <MiscIcon name="arrow-right" size="xs" class="!w-4 !h-4" />
                </MiscLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
const { product } = useProduct()

const descriptionState = ref(false)
const reviewsState = ref(false)
const greenfeedState = ref(true)
const warrantyState = ref(false)
const initReviews = ref(false)
const reviewsElement = ref()

const { warrantyData, hasWarrantyLabel } = useProductWarrantyLabel(product)
const { generateCompactLabel, generateFullLabel } = useWarrantyLabelSvg()

const generatedCompactSvg = ref<string | null>(null)
const generatedFullSvg = ref<string | null>(null)

// Generate compact SVG immediately, full SVG only when tab is expanded
watch(warrantyData, async (data) => {
    if (data?.useGeneratedSvg) {
        try {
            generatedCompactSvg.value = await generateCompactLabel(data.warrantyYears)
        } catch (error) {
            console.error('Failed to generate compact warranty label SVG:', error)
            generatedCompactSvg.value = null
        }
    } else {
        generatedCompactSvg.value = null
        generatedFullSvg.value = null
    }
}, { immediate: true })

// Lazy-load full SVG only when warranty tab is expanded
watch([warrantyData, warrantyState], async ([data, isExpanded]) => {
    if (data?.useGeneratedSvg && isExpanded && !generatedFullSvg.value) {
        try {
            generatedFullSvg.value = await generateFullLabel(
                data.brand,
                data.modelIdentifier,
                data.warrantyYears
            )
        } catch (error) {
            console.error('Failed to generate full warranty label SVG:', error)
            generatedFullSvg.value = null
        }
    }
})

watch(reviewsState, (value) => {
    if (!initReviews.value && value) {
        initReviews.value = true
    }
})

useRuntimeHook('productReviewsShow', eventListenerProductDetailReviews)

function eventListenerProductDetailReviews () {
    reviewsState.value = true

    nextTick(() => {
        reviewsElement?.value.scrollIntoView()
    })
}
</script>
